import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API_BASE, formatApiErrorDetail } from "@/lib/api";
import Player360 from "@/components/Player360";
import { useAuth } from "@/lib/auth";
import { AlertTriangle } from "lucide-react";

function detectMobile() {
  if (typeof window === "undefined") return false;
  const ua = navigator.userAgent || "";
  const narrow =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(max-width: 900px)").matches;
  const mobileUA = /Mobi|Android|iPhone|iPad|iPod/i.test(ua);
  return narrow || mobileUA;
}

/**
 * Public video page.
 *
 * Receiver-first design: NO platform branding, NO share row, NO admin
 * affordances. Just the player + title + description, with a minimal
 * "Powered by G. Kirchner" footer at the bottom.
 */
export default function VideoPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [errorStatus, setErrorStatus] = useState(0);

  const isMobile = useMemo(detectMobile, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await api.get(`/videos/${slug}`);
        if (!cancelled) setVideo(data);
      } catch (err) {
        if (!cancelled) {
          const code = err.response?.status || 0;
          setErrorStatus(code);
          if (code === 404) setError("Video nicht gefunden oder privat");
          else setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  // Poll status while pending (max 5 min, every 5s).
  useEffect(() => {
    if (!video || video.processing_status !== "pending") return undefined;
    let cancelled = false;
    let polled = 0;
    const tick = async () => {
      if (cancelled) return;
      polled += 1;
      try {
        const { data } = await api.get(`/videos/${slug}/status`);
        if (cancelled) return;
        setVideo((prev) =>
          prev
            ? {
                ...prev,
                processing_status: data.processing_status,
                mobile_ready: data.mobile_ready,
                processing_error: data.processing_error,
              }
            : prev
        );
        if (data.processing_status === "pending" && polled < 60) {
          setTimeout(tick, 5000);
        }
      } catch {
        /* swallow blip */
      }
    };
    const t = setTimeout(tick, 5000);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, video?.processing_status]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0b] text-[#9a9aa3] flex items-center justify-center text-sm">
        Lädt...
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0a0b] text-white flex flex-col">
        <main className="flex-1 flex items-center justify-center px-6">
          <div className="text-center space-y-3 max-w-md" data-testid="video-error">
            <div className="inline-flex w-14 h-14 items-center justify-center rounded-2xl bg-[#1f1f23] mb-2">
              <AlertTriangle size={26} className="text-[#9a9aa3]" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight">{error}</h1>
            <p className="text-sm text-[#9a9aa3]">
              {errorStatus === 404
                ? "Der Link ist möglicherweise abgelaufen oder das Video wurde auf privat gestellt."
                : `Slug: ${slug}`}
            </p>
          </div>
        </main>
      </div>
    );
  }

  const useMobileStream = isMobile && video.mobile_ready;
  const streamUrl = `${API_BASE}/videos/${slug}/stream${useMobileStream ? "?q=mobile" : ""}`;

  let playerNotice = "";
  if (isMobile && video.processing_status === "pending") {
    playerNotice =
      "Wird für Mobile optimiert (je nach Videogröße einige Minuten). Du kannst es jetzt schon ansehen — es kann ruckeln.";
  }

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white flex flex-col">
      <main className="flex-1 max-w-5xl w-full mx-auto px-4 sm:px-6 pt-6 sm:pt-10 pb-12">
        <Player360
          src={streamUrl}
          notice={playerNotice}
          hudOverlay={video.hud_overlay || null}
          poiAnnotations={video.poi_annotations || []}
          textOverlays={video.text_overlays || []}
          showBackButton={!!user}
          onBack={() => navigate("/admin")}
        />

        <div className="mt-6">
          <h1
            className="text-2xl sm:text-3xl font-bold tracking-tight"
            data-testid="video-title"
          >
            {video.title}
          </h1>
          {video.description && (
            <p
              className="text-[#9a9aa3] mt-2 leading-relaxed whitespace-pre-line"
              data-testid="video-description"
            >
              {video.description}
            </p>
          )}
        </div>
      </main>
    </div>
  );
}
